import React, { useState, useEffect } from 'react';
import ArtistLayout from '../../components/artist/ArtistLayout';
import { Button } from '../../components/ui/button';
import { Plus, Clock, CheckCircle, XCircle, X, Upload } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Master data
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [colours, setColours] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    short_description: '',
    features: [''],
    category_id: '',
    material_id: '',
    color_variants: []
  });

  useEffect(() => {
    fetchCards();
    fetchMasterData();
  }, []);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('artist_token');
      
      const response = await apiRequest(`${BACKEND_URL}/api/artist/cards/digital-cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setCards(response.data.cards || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch cards:', err);
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      // Fetch categories
      const categoriesRes = await apiRequest(`${BACKEND_URL}/api/admin/public/card-categories`, {
        method: 'GET',
      });
      if (categoriesRes.ok) {
        setCategories(categoriesRes.data.categories || []);
      }

      // Fetch materials
      const materialsRes = await apiRequest(`${BACKEND_URL}/api/admin/public/materials`, {
        method: 'GET',
      });
      if (materialsRes.ok) {
        setMaterials(materialsRes.data.materials || []);
      }

      // Fetch colours
      const coloursRes = await apiRequest(`${BACKEND_URL}/api/admin/public/colours`, {
        method: 'GET',
      });
      if (coloursRes.ok) {
        setColours(coloursRes.data.colours || []);
      }
    } catch (err) {
      console.error('Failed to fetch master data:', err);
    }
  };

  const handleAddCard = () => {
    setFormData({
      name: '',
      short_description: '',
      features: [''],
      category_id: '',
      material_id: '',
      color_variants: []
    });
    setShowModal(true);
  };

  const handleAddFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const handleRemoveFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const handleAddColorVariant = () => {
    setFormData({
      ...formData,
      color_variants: [
        ...formData.color_variants,
        {
          colour_id: '',
          price: '',
          front_design_url: null,
          back_design_url: null
        }
      ]
    });
  };

  const handleRemoveColorVariant = (index) => {
    const newVariants = formData.color_variants.filter((_, i) => i !== index);
    setFormData({ ...formData, color_variants: newVariants });
  };

  const handleColorVariantChange = (index, field, value) => {
    const newVariants = [...formData.color_variants];
    newVariants[index][field] = value;
    setFormData({ ...formData, color_variants: newVariants });
  };

  const handleFileChange = (index, field, file) => {
    const newVariants = [...formData.color_variants];
    newVariants[index][field] = file;
    setFormData({ ...formData, color_variants: newVariants });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('artist_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('short_description', formData.short_description);
      formDataToSend.append('features', JSON.stringify(formData.features.filter(f => f.trim() !== '')));
      formDataToSend.append('category_id', formData.category_id || '');
      formDataToSend.append('material_id', formData.material_id);
      
      // Prepare color variants
      const colorVariantsData = formData.color_variants.map((variant, index) => {
        // Upload images and get URLs
        if (variant.front_design_url instanceof File) {
          formDataToSend.append(`front_design_${index}`, variant.front_design_url);
        }
        if (variant.back_design_url instanceof File) {
          formDataToSend.append(`back_design_${index}`, variant.back_design_url);
        }
        
        return {
          colour_id: variant.colour_id,
          price: parseFloat(variant.price),
          front_index: index,
          back_index: index
        };
      });
      
      formDataToSend.append('color_variants', JSON.stringify(colorVariantsData));

      const response = await fetch(`${BACKEND_URL}/api/artist/cards/digital-cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        alert('Card submitted successfully! It will be reviewed by admin.');
        setShowModal(false);
        fetchCards();
      } else {
        const error = await response.json();
        alert(`Failed to create card: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error creating card:', err);
      alert('Error creating card');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      published: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  return (
    <ArtistLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-navy">My Digital Cards</h1>
            <p className="text-sm text-gray-600 mt-1">All new cards require admin approval before going live</p>
          </div>
          <Button 
            onClick={handleAddCard}
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading cards...</p>
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <p className="text-gray-600 mb-4">No digital cards found</p>
            <p className="text-sm text-gray-500 mb-4">Click "Add Card" to create your first digital card</p>
            <Button 
              onClick={handleAddCard}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Card
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div
                key={card.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Card Image */}
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  {card.color_variants && card.color_variants.length > 0 && card.color_variants[0].front_design_url ? (
                    <img
                      src={`${BACKEND_URL}${card.color_variants[0].front_design_url}`}
                      alt={card.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <span className="text-gray-400">No Image</span>
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-navy">{card.name}</h3>
                    {getStatusBadge(card.status)}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {card.short_description}
                  </p>
                  
                  <p className="text-xs text-gray-400">
                    Created: {new Date(card.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Card Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-navy">Add Digital Card</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Card Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Category</label>
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Short Description *</label>
                  <textarea
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Material *</label>
                  <select
                    value={formData.material_id}
                    onChange={(e) => setFormData({ ...formData, material_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300"
                    required
                  >
                    <option value="">Select Material</option>
                    {materials.map(mat => (
                      <option key={mat.id} value={mat.id}>{mat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Features */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Features</label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300"
                        placeholder="Feature description"
                      />
                      {formData.features.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => handleRemoveFeature(index)}
                          variant="outline"
                          className="border-red-300 text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    onClick={handleAddFeature}
                    variant="outline"
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </div>

                {/* Color Variants */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Color Variants *</label>
                  {formData.color_variants.map((variant, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold">Variant {index + 1}</h4>
                        <Button
                          type="button"
                          onClick={() => handleRemoveColorVariant(index)}
                          variant="outline"
                          className="border-red-300 text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Color *</label>
                          <select
                            value={variant.colour_id}
                            onChange={(e) => handleColorVariantChange(index, 'colour_id', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300"
                            required
                          >
                            <option value="">Select Color</option>
                            {colours.map(color => (
                              <option key={color.id} value={color.id}>{color.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Price *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={variant.price}
                            onChange={(e) => handleColorVariantChange(index, 'price', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300"
                            placeholder="0.00"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Front Design *</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(index, 'front_design_url', e.target.files[0])}
                            className="w-full"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Back Design *</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(index, 'back_design_url', e.target.files[0])}
                            className="w-full"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    onClick={handleAddColorVariant}
                    variant="outline"
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Color Variant
                  </Button>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-purple-600 text-white hover:bg-purple-700"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ArtistLayout>
  );
}